/**
 * Font utilities for checking local fonts and loading Google Fonts
 */
import { FONT_CONFIG } from '../../constants/textConstant';

// List of available fonts in the system
const AVAILABLE_FONTS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Georgia',
  'Palatino',
  'Garamond',
  'Bookman',
  'Comic Sans MS',
  'Trebuchet MS',
  'Arial Black',
  'Impact',
  'Tahoma',
  'Century Gothic',
  'Lucida Sans Unicode',
  'Lucida Console',
  'Monaco',
  'Consolas',
  'Courier',
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
];

// Google Fonts mapping for fallback
const GOOGLE_FONTS_MAP = {
  Basic: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
  Oswald: 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap',
};

/**
 * Check if a font is available locally
 * @param {string} fontName - The font name to check
 * @returns {Promise<boolean>} - True if font is available locally
 */
export const checkLocalFont = (fontName) => {
  return new Promise((resolve) => {
    // Create a test element
    const testElement = document.createElement('span');
    testElement.style.fontSize = '72px';
    testElement.style.fontFamily = fontName;
    testElement.innerHTML = 'mmmmmmmmmmlli';

    // Add to DOM temporarily
    testElement.style.position = 'absolute';
    testElement.style.left = '-9999px';
    testElement.style.top = '-9999px';
    document.body.appendChild(testElement);

    // Get computed style
    const computedStyle = window.getComputedStyle(testElement);
    const fontFamily = computedStyle.fontFamily;

    // Clean up
    document.body.removeChild(testElement);

    // Check if font is actually loaded
    const isLoaded =
      fontFamily.includes(fontName) &&
      !fontFamily.includes('Arial') &&
      !fontFamily.includes('sans-serif');

    resolve(isLoaded);
  });
};

/**
 * Load Google Font dynamically
 * @param {string} fontName - The font name to load
 * @returns {Promise<boolean>} - True if font loaded successfully
 */
export const loadGoogleFont = (fontName) => {
  return new Promise((resolve) => {
    // Map font names to their actual Google Font names for loading
    const fontNameMap = {
      Basic: 'Roboto',
    };
    const actualFontName = fontNameMap[fontName] || fontName;
    
    // Check if font is already loaded (check both original and mapped name)
    if (document.querySelector(`link[href*="${actualFontName}"]`) || 
        document.querySelector(`link[href*="${fontName}"]`)) {
      resolve(true);
      return;
    }

    // Get Google Fonts URL (use original fontName to check GOOGLE_FONTS_MAP)
    const fontUrl = GOOGLE_FONTS_MAP[fontName];
    if (!fontUrl) {
      console.warn(`Google Font not available: ${fontName}`);
      resolve(false);
      return;
    }

    // Create link element
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    link.onload = () => {
      console.log(`Google Font loaded: ${fontName}`);
      resolve(true);
    };
    link.onerror = () => {
      console.error(`Failed to load Google Font: ${fontName}`);
      resolve(false);
    };

    // Add to head
    document.head.appendChild(link);
  });
};

/**
 * Get font with fallback - check local first, then load from Google Fonts
 * @param {string} fontName - The font name to get (can be font name like "Basic" or font-family string)
 * @returns {Promise<string>} - The font-family string to use (with fallback)
 */
export const getFontWithFallback = async (fontName) => {
  // If it's already a font-family string (contains comma), return as is after loading font
  if (fontName.includes(',')) {
    // Extract the first font name from the font-family string
    const firstFontName = fontName.split(',')[0].trim();
    // Load the font if it's a Google Font
    await loadGoogleFont(firstFontName);
    return fontName;
  }
  
  // Map font names to their actual Google Font names
  const fontNameMap = {
    Basic: 'Roboto',
  };
  
  // Get the actual font name to load (e.g., "Basic" -> "Roboto")
  const actualFontName = fontNameMap[fontName] || fontName;
  
  // Check if it's a system font
  if (AVAILABLE_FONTS.includes(actualFontName)) {
    return actualFontName;
  }

  // Check if font is available locally
  const isLocal = await checkLocalFont(actualFontName);
  if (isLocal) {
    return actualFontName;
  }

  // Try to load from Google Fonts
  const loaded = await loadGoogleFont(fontName); // Use original fontName to check GOOGLE_FONTS_MAP
  if (loaded) {
    // Get the full font-family string from FONT_CONFIG if available
    const fontConfig = FONT_CONFIG.find((f) => f.name === fontName);
    if (fontConfig) {
      return fontConfig.family;
    }
    // Return the actual font name (e.g., "Roboto" instead of "Basic")
    return actualFontName;
  }

  // Fallback to system font
  console.warn(`Font not available: ${fontName}, using fallback`);
  return 'Arial';
};

/**
 * Preload multiple fonts
 * @param {string[]} fontNames - Array of font names to preload
 * @returns {Promise<string[]>} - Array of successfully loaded fonts
 */
export const preloadFonts = async (fontNames) => {
  const loadedFonts = [];

  for (const fontName of fontNames) {
    try {
      const font = await getFontWithFallback(fontName);
      loadedFonts.push(font);
    } catch (error) {
      console.error(`Failed to load font: ${fontName}`, error);
    }
  }

  return loadedFonts;
};

/**
 * Get all available fonts (local + Google Fonts)
 * @returns {string[]} - Array of all available font names
 */
export const getAllAvailableFonts = () => {
  return [...AVAILABLE_FONTS, ...Object.keys(GOOGLE_FONTS_MAP)];
};

/**
 * Check if a font is a Google Font
 * @param {string} fontName - The font name to check
 * @returns {boolean} - True if it's a Google Font
 */
export const isGoogleFont = (fontName) => {
  return Object.keys(GOOGLE_FONTS_MAP).includes(fontName);
};

/**
 * Get Google Font URL
 * @param {string} fontName - The font name
 * @returns {string|null} - Google Font URL or null
 */
export const getGoogleFontUrl = (fontName) => {
  return GOOGLE_FONTS_MAP[fontName] || null;
};
